/**
 * Platform controls for one school's AI - the site administrator's part, and
 * only that part.
 *
 * This panel used to be the whole AI console: the school's switch, its access
 * mode, its provider key, its models, its quotas and its prompts. Since AI
 * became the school's own decision (DR-029) all of that also appears in the
 * school's own tab (features/school/AiSettings.tsx), and both were rendered on
 * the same screen for a site administrator - so the bring-your-own-key form
 * appeared twice on one page, with two Save buttons writing the same fields.
 * Whichever one you used, the other was stale.
 *
 * The rule now is that nothing appears here that the school's own panel already
 * shows. What is left is genuinely the platform's:
 *
 *   - **Suspension.** TeachCraftAI's stop, for abuse or non-payment. It
 *     overrides the school's choice and leaves their settings untouched.
 *   - **Monthly allowances.** Cost control on the platform's own provider
 *     account, so a school cannot set them.
 *   - **Wording, and the picture size ceiling.** House style and the print
 *     pipeline's limit (DR-013) - support territory, not a school preference.
 *
 * Which features exist at all is a site-wide decision and lives in
 * Site settings → AI Capabilities, not here.
 */

import { useState } from 'react'
import type { ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Button, Checkbox, Field, Input, Pill, Spinner, Textarea } from '../../components/ui'
import { toast } from '../../components/toast'
import { api, type AiSchoolSettings } from '../../lib/api/client'

/** Matches MIN/MAX_GENERATED_IMAGE_PX in app/services/ai_settings.py. Stated
 *  here so the field can say the range before the server refuses it. */
const MIN_IMAGE_PX = 300
const MAX_IMAGE_PX = 1240

export function AiControl({ schoolId }: { schoolId: string }) {
  const queryClient = useQueryClient()
  const settingsQuery = useQuery({
    queryKey: ['platform', 'ai', schoolId],
    queryFn: () => api.schoolAiSettings(schoolId),
  })

  const onError = (error: unknown) =>
    toast(error instanceof Error ? error.message : 'That could not be saved.', 'error')
  const applied = (updated: AiSchoolSettings) => {
    queryClient.setQueryData(['platform', 'ai', schoolId], updated)
    // The school's own panel is on the same screen and reads the same row.
    queryClient.invalidateQueries({ queryKey: ['ai-billing'] })
  }

  const setSuspended = useMutation({
    mutationFn: (suspended: boolean) => api.setAiEnabled(schoolId, !suspended),
    onSuccess: (updated) => {
      applied(updated)
      toast(
        updated.enabled_by_site_admin
          ? 'AI restored for this school.'
          : 'AI suspended for this school.',
      )
    },
    onError,
  })

  if (settingsQuery.isLoading) return <Spinner label="Loading platform controls" />
  if (settingsQuery.isError) {
    return (
      <div className="tc-ai-platform" role="alert">
        <p className="tc-ai-help">
          {settingsQuery.error instanceof Error
            ? settingsQuery.error.message
            : 'Platform controls could not be loaded.'}
        </p>
      </div>
    )
  }
  const settings = settingsQuery.data
  if (!settings) return null
  const suspended = !settings.enabled_by_site_admin

  return (
    <section className="tc-ai-platform" aria-labelledby="tc-ai-platform-title">
      <header className="tc-ai-platform-head">
        <div>
          <h2 id="tc-ai-platform-title">Platform controls</h2>
          <p className="tc-ai-help">
            Cost and safety limits this school cannot set for itself. Everything else about their
            AI is in the sections above, and is theirs to change.
          </p>
        </div>
        <Pill tone="secondary">Site admin only</Pill>
      </header>

      <div className="tc-ai-platform-body">
        <div className={`tc-ai-suspend ${suspended ? 'is-on' : ''}`}>
          <Checkbox
            label="Suspend AI for this school"
            checked={suspended}
            disabled={setSuspended.isPending}
            onChange={(event) => setSuspended.mutate(event.target.checked)}
          />
          <p className="tc-ai-help">
            For abuse or non-payment. Overrides the school's own switch and keeps their settings
            as they are, so restoring is one click.
          </p>
        </div>

        <div className="tc-ai-limit-grid">
          <Limits schoolId={schoolId} settings={settings} onApplied={applied} />
        </div>

        <p className="tc-ai-help">
          Which AI features exist at all is site-wide:{' '}
          <Link to="/site-settings?tab=ai">Site settings → AI Capabilities</Link>.
        </p>
      </div>
    </section>
  )
}

/** One editable value with its own Save, because these are independent
 *  settings a support call changes one at a time - a single Save for the whole
 *  panel would make "raise their picture allowance" also re-submit a prompt
 *  someone was midway through editing. */
function Saveable({
  label,
  hint,
  dirty,
  pending,
  onSave,
  children,
}: {
  label: string
  hint?: string
  dirty: boolean
  pending: boolean
  onSave: () => void
  children: ReactNode
}) {
  return (
    <div className="tc-ai-saveable">
      <Field label={label} hint={hint}>
        {children}
      </Field>
      <Button variant="secondary" disabled={!dirty || pending} onClick={onSave}>
        Save
      </Button>
    </div>
  )
}

function Limits({
  schoolId,
  settings,
  onApplied,
}: {
  schoolId: string
  settings: AiSchoolSettings
  onApplied: (updated: AiSchoolSettings) => void
}) {
  const [textQuota, setTextQuota] = useState(String(settings.monthly_text_quota))
  const [imageQuota, setImageQuota] = useState(String(settings.monthly_image_quota))
  const [maxPx, setMaxPx] = useState(String(settings.max_generated_image_px))
  const [grammar, setGrammar] = useState(settings.grammar_prompt_override ?? '')
  const [imagePrefix, setImagePrefix] = useState(settings.image_prompt_prefix ?? '')

  const onError = (error: unknown) =>
    toast(error instanceof Error ? error.message : 'That could not be saved.', 'error')

  const saveQuota = useMutation({
    mutationFn: () => api.setAiQuota(schoolId, Number(imageQuota), Number(textQuota)),
    onSuccess: (updated) => {
      onApplied(updated)
      toast('Monthly allowance saved.')
    },
    onError,
  })
  const saveLimit = useMutation({
    mutationFn: () => api.setAiImageLimit(schoolId, Number(maxPx)),
    onSuccess: (updated) => {
      onApplied(updated)
      toast('Picture size limit saved.')
    },
    onError,
  })
  const savePrompts = useMutation({
    mutationFn: () => api.setAiPrompts(schoolId, grammar || null, imagePrefix || null),
    onSuccess: (updated) => {
      onApplied(updated)
      toast('Wording saved.')
    },
    onError,
  })

  const quotaDirty =
    Number(textQuota) !== settings.monthly_text_quota ||
    Number(imageQuota) !== settings.monthly_image_quota
  const promptsDirty =
    grammar !== (settings.grammar_prompt_override ?? '') ||
    imagePrefix !== (settings.image_prompt_prefix ?? '')

  return (
    <>
      <Saveable
        label="Monthly allowance"
        hint="Rephrasings and pictures per month, on the platform subscription. A school on its own key is not metered here."
        dirty={quotaDirty}
        pending={saveQuota.isPending}
        onSave={() => saveQuota.mutate()}
      >
        <div className="tc-ai-quota-pair">
          <label>
            <span>Rephrasings</span>
            <Input
              type="number"
              min={0}
              value={textQuota}
              onChange={(event) => setTextQuota(event.target.value)}
            />
          </label>
          <label>
            <span>Pictures</span>
            <Input
              type="number"
              min={0}
              value={imageQuota}
              onChange={(event) => setImageQuota(event.target.value)}
            />
          </label>
        </div>
      </Saveable>

      <Saveable
        label="Largest generated picture"
        hint={`${MIN_IMAGE_PX}-${MAX_IMAGE_PX} pixels. The print pipeline keeps no more detail than the ceiling (DR-013).`}
        dirty={Number(maxPx) !== settings.max_generated_image_px}
        pending={saveLimit.isPending}
        onSave={() => saveLimit.mutate()}
      >
        <Input
          type="number"
          min={MIN_IMAGE_PX}
          max={MAX_IMAGE_PX}
          value={maxPx}
          onChange={(event) => setMaxPx(event.target.value)}
        />
      </Saveable>

      {/* The wording is the behaviour, so the built-in is shown as placeholder
          rather than described - a summary of a prompt is not the prompt. */}
      <Saveable
        label="Rephrasing instruction"
        hint="Blank uses the built-in wording, shown below."
        dirty={promptsDirty}
        pending={savePrompts.isPending}
        onSave={() => savePrompts.mutate()}
      >
        <Textarea
          rows={3}
          value={grammar}
          placeholder={settings.default_grammar_instruction}
          onChange={(event) => setGrammar(event.target.value)}
        />
      </Saveable>

      <Saveable
        label="Picture style"
        hint="Prepended to every description a teacher writes. Blank uses the built-in style."
        dirty={promptsDirty}
        pending={savePrompts.isPending}
        onSave={() => savePrompts.mutate()}
      >
        <Textarea
          rows={3}
          value={imagePrefix}
          placeholder={settings.default_image_prefix}
          onChange={(event) => setImagePrefix(event.target.value)}
        />
      </Saveable>
    </>
  )
}
